/**
 * How strong a password is, and what to call it.
 *
 * 🔴 WHY THIS IS ITS OWN MODULE. The ladder is the one claim the site makes about a password,
 * and it was three `if` lines inside a component — untestable without rendering the page and
 * pressing a control. George, 19 September 2026: *"for this site in particular its Fortress
 * from 32, 48, 64 please create something for 48 and 64 that are unique."*
 *
 * He was right, and it was worse than it looked: **32, 48 AND 64 characters all read
 * "Fortress"**, because the top of the ladder was 120 bits and even the smallest sensible
 * password here clears it. The scale said nothing about the one difference a reader is
 * actually choosing between. There are now six rungs, and the top two are the sizes that
 * needed them.
 */

/**
 * The alphabet sizes the generator draws from. `symbols` is the site's own switch.
 */
export const entropyBits = (size, symbols) => {
  const chars = symbols ? 85 : 62
  return Math.round(size * Math.log2(chars))
}

/**
 * The ladder, highest first. Thresholds are chosen so every size the site offers lands on the
 * rung it should in BOTH modes — with symbols (85 characters) and without (62):
 *
 *   size   with symbols   without   reads as
 *     8         51           48     Weak
 *    16        103           95     Strong
 *    32        205          190     Fortress
 *    48        308          286     Vault
 *    64        410          381     Citadel
 */
export const STRENGTH_LEVELS = [
  { label: 'Citadel', level: 6, minBits: 350 },
  { label: 'Vault', level: 5, minBits: 250 },
  { label: 'Fortress', level: 4, minBits: 120 },
  { label: 'Strong', level: 3, minBits: 90 },
  { label: 'Fair', level: 2, minBits: 60 },
  { label: 'Weak', level: 1, minBits: 0 }
]

export const strengthOf = (bits) =>
  STRENGTH_LEVELS.find((rung) => bits >= rung.minBits) || STRENGTH_LEVELS[STRENGTH_LEVELS.length - 1]

/**
 * The full scale the meter is drawn against — the entropy of the largest password the site
 * offers WITH symbols. It used to be 140, so 32, 48 and 64 all filled the bar to 100% and the
 * bar could not show the difference the labels now name.
 */
export const FULL_SCALE_BITS = entropyBits(64, true)

export const meterWidth = (bits) => Math.min(100, (bits / FULL_SCALE_BITS) * 100)
